import type { ComponentPropsWithoutRef } from "react";
import { useMemo } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export type SubTabProps = Omit<ComponentPropsWithoutRef<"div">, "role"> & {
  /** 스크린리더용 탭 목록 레이블 */
  "aria-label"?: string;
};

export function SubTab({
  children,
  className,
  "aria-label": ariaLabel = "서브 탭",
  ...rest
}: SubTabProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "mb-5 flex items-center justify-center gap-10 border-b border-sig text-center",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

type SubTabItemShared = {
  label: string;
  value: number;
  disabled?: boolean;
  className?: string;
};

/** `to`만 넘기면 클릭 시 `navigate(to)` + URL로 활성 판별 (`end`는 `matchPath`용) */
type SubTabItemRouteProps = SubTabItemShared & {
  to: string;
  end?: boolean;
  /** true면 이동 시 현재 URL의 `search`(쿼리)를 그대로 붙임 — 탭 전환 시 연·월 등 유지 */
  preserveSearch?: boolean;
  onSelect?: (value: number) => void;
};

/** 로컬 상태 탭 — `isActive` / `onSelect` */
type SubTabItemStateProps = SubTabItemShared & {
  to?: undefined;
  isActive: boolean;
  onSelect: (value: number) => void;
};

export type SubTabItemProps = SubTabItemRouteProps | SubTabItemStateProps;

function subTabItemVisualClassName(
  isActive: boolean,
  extra?: { disabled?: boolean; className?: string }
) {
  return cn(
    "relative flex shrink-0 items-center gap-[0.2em] px-4 pb-3 pt-0 font-pretendard text-2xl leading-none",
    "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-1 after:content-[''] after:transition-colors after:duration-500",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/40 focus-visible:ring-offset-2",
    isActive
      ? "font-bold text-sig after:block after:bg-sig"
      : "text-[#939399] after:hidden after:bg-[#ceced4] hover:after:block",
    extra?.disabled && "pointer-events-none cursor-not-allowed opacity-50",
    extra?.className
  );
}

export function SubTabItem(props: SubTabItemProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const routeTo = "to" in props && props.to ? props.to : null;
  const routeEnd = "to" in props && props.to ? (props.end ?? false) : false;
  const preserveSearch =
    "to" in props && props.to && "preserveSearch" in props
      ? (props as SubTabItemRouteProps).preserveSearch
      : false;
  const stateIsActive =
    routeTo == null ? (props as SubTabItemStateProps).isActive : false;

  const pathnameNorm = location.pathname.replace(/\/$/, "") || "/";

  const isActive = useMemo(() => {
    if (routeTo != null) {
      return matchPath({ path: routeTo, end: routeEnd }, pathnameNorm) != null;
    }
    return stateIsActive;
  }, [routeTo, routeEnd, pathnameNorm, stateIsActive]);

  const handleClick = () => {
    if (props.disabled) return;
    if (routeTo != null) {
      void navigate(
        preserveSearch
          ? { pathname: routeTo, search: location.search }
          : routeTo
      );
      props.onSelect?.(props.value);
      return;
    }
    (props as SubTabItemStateProps).onSelect(props.value);
  };

  return (
    <button
      type="button"
      role="tab"
      id={`subtab-${props.value}`}
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      disabled={props.disabled}
      onClick={handleClick}
      className={subTabItemVisualClassName(isActive, {
        disabled: props.disabled,
        className: props.className,
      })}
    >
      <div className="grow text-center text-2xl font-pretendard">
        {props.label}
      </div>
    </button>
  );
}
